import { useEffect, useRef, useState } from "react";
import {
  getDraws,
  getDrawFullDetail,
  getDrawTimeline,
  getDrawInspection,
} from "services/drawService";
import { useToast } from "Components/Common/Toast/ToastContext";
import { useDrawHistoryPdf } from "hooks/useDrawHistoryPdf";
import { useG702Pdf } from "hooks/useG702Pdf";
import { useInspectionReportPdf } from "hooks/useInspectionReportPdf";
import { useG703Excel } from "hooks/useG703Excel";
import DrawSelector from "./components/DrawSelector";
import DrawHeader from "./components/DrawHeader";
import SummaryTab from "./components/SummaryTab";
import G703Tab from "./components/G703Tab";
import InvoicesTab from "./components/InvoicesTab";
import WaiversTab from "./components/WaiversTab";
import ProgressTab from "./components/ProgressTab";
import SignaturesTab from "./components/SignaturesTab";
import InspectionTab from "./components/InspectionTab";
import TimelineTab from "./components/TimelineTab";
import ApprovalTab from "./components/ApprovalTab";
import {
  AiaTabId,
  DrawDetail,
  DrawListItem,
  DrawTimelineEvent,
  DrawInspectionData,
  buildGcProgressDisplayItems,
} from "./DrawHistory.types";
import "./DrawHistory.scss";
import EmptyState from "Components/Common/EmptyState";
import DrawRequestIcon from "Assets/Img/DrawRequestIcon";

interface DrawHistorySectionProps {
  isActive: boolean;
  activeTab: AiaTabId;
  projectId: string;
  projectName?: string;
  onBadgesChange?: (invoiceCount: number, waiverProgress: string) => void;
}

const DrawHistorySection = ({
  isActive,
  activeTab,
  projectId,
  projectName,
  onBadgesChange,
}: DrawHistorySectionProps) => {
  const showToast = useToast();
  const fetchedListRef = useRef(false);
  const {
    exportDrawHistoryPdf,
    isExportingPdf,
    exportAllDrawsPdf,
    isExportingAll,
  } = useDrawHistoryPdf();
  const { exportG702Pdf, isExportingG702 } = useG702Pdf();
  const { viewInspectionReportPdf, isViewingReport } = useInspectionReportPdf();
  const { exportG703Excel, isExportingExcel } = useG703Excel();

  const [draws, setDraws] = useState<DrawListItem[]>([]);
  const [drawsLoading, setDrawsLoading] = useState(true);
  const [selectedDrawId, setSelectedDrawId] = useState("");

  const [drawDetail, setDrawDetail] = useState<DrawDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // TimeLine states
  const [timelineEvents, setTimelineEvents] = useState<DrawTimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  // Inspection states
  const [inspectionData, setInspectionData] =
    useState<DrawInspectionData | null>(null);
  const [inspectionLoading, setInspectionLoading] = useState(false);

  // Load the draw list once the tab is first opened
  useEffect(() => {
    if (!isActive || !projectId || fetchedListRef.current) return;
    fetchedListRef.current = true;
    setDrawsLoading(true);
    (getDraws(projectId) as Promise<{ data?: any; error?: any }>).then(
      ({ data, error }) => {
        const list: DrawListItem[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        const sorted = [...list].sort((a, b) => a.drawNumber - b.drawNumber);
        setDraws(sorted);
        if (!error && sorted.length > 0) {
          setSelectedDrawId(sorted[sorted.length - 1].id);
        }
        setDrawsLoading(false);
      },
    );
  }, [isActive, projectId]);

  // Load the selected draw's full detail (G702 + G703 + waivers + signatures + approval)
  useEffect(() => {
    if (!projectId || !selectedDrawId) return;
    setDetailLoading(true);
    (
      getDrawFullDetail(projectId, selectedDrawId) as Promise<{
        data?: any;
        error?: any;
      }>
    ).then(({ data }) => {
      setDrawDetail(data?.data ?? null);
      setDetailLoading(false);
    });
  }, [projectId, selectedDrawId]);

  // Timeline API
  useEffect(() => {
    if (activeTab !== "timeline" || !projectId || !selectedDrawId) return;
    setTimelineLoading(true);
    (
      getDrawTimeline(projectId, selectedDrawId) as Promise<{
        data?: any;
        error?: any;
      }>
    ).then(({ data, error }) => {
      const payload = data?.data;
      const list: DrawTimelineEvent[] = Array.isArray(payload?.events)
        ? payload.events
        : Array.isArray(payload)
          ? payload
          : [];
      setTimelineEvents(error ? [] : list);
      setTimelineLoading(false);
    });
  }, [activeTab, projectId, selectedDrawId]);

  // Inspection API
  useEffect(() => {
    if (activeTab !== "inspection" || !projectId || !selectedDrawId) return;
    setInspectionLoading(true);
    (
      getDrawInspection(projectId, selectedDrawId) as Promise<{
        data?: any;
        error?: any;
      }>
    ).then(({ data, error }) => {
      setInspectionData(error ? null : (data?.data ?? null));
      setInspectionLoading(false);
    });
  }, [activeTab, projectId, selectedDrawId]);

  const invoiceCount = (drawDetail?.lienWaivers?.items ?? []).filter(
    (item) => item.invoice,
  ).length;
  const signedWaivers = drawDetail?.lienWaivers?.signed ?? 0;
  const totalWaivers = drawDetail?.lienWaivers?.total ?? 0;
  const waiverProgress =
    totalWaivers > 0 ? `${signedWaivers}/${totalWaivers}` : "";

  // Report the derived badge counts up so the parent's sidebar subItems can
  // display them (invoiceCount/waiverProgress live here since this is the
  // component that owns the draw-detail fetch).
  useEffect(() => {
    onBadgesChange?.(invoiceCount, waiverProgress);
  }, [invoiceCount, waiverProgress, onBadgesChange]);

  const toastExport = (label: string) => showToast(`Downloading ${label}…`);

  const handleExportPdf = async () => {
    if (!drawDetail || isExportingPdf) return;
    const drawLabel = String(drawDetail.draw.drawNumber).padStart(2, "0");
    toastExport(`Draw #${drawLabel} package as PDF`);
    try {
      await exportDrawHistoryPdf(
        projectId,
        drawDetail,
        projectName,
        buildGcProgressDisplayItems(drawDetail),
        showToast,
      );
    } catch {
      showToast("Failed to generate PDF. Please try again.");
    }
  };

  const handleExportG702 = async () => {
    if (!drawDetail || isExportingG702) return;
    toastExport("G702 PDF");
    try {
      await exportG702Pdf(drawDetail, projectName);
    } catch {
      showToast("Failed to generate PDF. Please try again.");
    }
  };

  const handleViewReport = async () => {
    if (isViewingReport) return;
    if (!inspectionData?.inspection) {
      showToast("No CSC inspection report available for this draw.");
      return;
    }
    try {
      await viewInspectionReportPdf(
        inspectionData,
        drawDetail?.draw.drawNumber ?? 0,
        projectName,
      );
    } catch {
      showToast("Failed to open report.");
    }
  };

  const handleExportExcel = async () => {
    if (!drawDetail || isExportingExcel) return;
    toastExport("G703 Excel");
    try {
      await exportG703Excel(drawDetail, projectName);
    } catch {
      showToast("Failed to generate Excel file. Please try again.");
    }
  };

  const handleExportAll = async () => {
    if (draws.length === 0 || isExportingAll) return;
    try {
      await exportAllDrawsPdf(projectId, draws, projectName, showToast);
    } catch {
      showToast("Failed to generate PDF. Please try again.");
    }
  };

  // Admin's ProjectDetail has no ".section { display:none }" CSS (unlike the
  // GC module this was ported from) — SOVSetup, the sibling always-mounted
  // section here, hides itself the same way: return null when inactive.
  if (!isActive) return null;

  return (
    <div className="section active" id="sec-aia-billing-summary">
      <div className="sectionTitle">Draw History</div>
      <div className="sectionSub">
        Draw-by-draw AIA G702/G703 billing detail, invoices, lien waivers,
        inspection, and approval history.
      </div>

      <DrawSelector
        draws={draws}
        selectedDrawId={selectedDrawId}
        onSelectDraw={setSelectedDrawId}
        onExportPdf={handleExportPdf}
        onExportAll={handleExportAll}
        exportingPdf={isExportingPdf}
        exportingAll={isExportingAll}
      />

      {!drawsLoading && draws.length === 0 ? (
        <EmptyState
          noBorder
          icon={<DrawRequestIcon />}
          title="No draws have been submitted for this project yet."
        />
      ) : (
        <>
          <DrawHeader
            projectName={projectName}
            drawDetail={drawDetail}
            loading={detailLoading && !drawDetail}
          />

          <div className="aiaMain">
            {activeTab === "summary" && (
              <SummaryTab
                drawDetail={drawDetail}
                loading={detailLoading}
                onExportG702={handleExportG702}
                exportingG702={isExportingG702}
              />
            )}
            {activeTab === "g703" && (
              <G703Tab
                drawDetail={drawDetail}
                loading={detailLoading}
                onExportExcel={handleExportExcel}
                exportingExcel={isExportingExcel}
              />
            )}
            {activeTab === "invoices" && (
              <InvoicesTab
                drawDetail={drawDetail}
                loading={detailLoading}
                projectId={projectId}
              />
            )}
            {activeTab === "waivers" && (
              <WaiversTab drawDetail={drawDetail} loading={detailLoading} />
            )}
            {activeTab === "progress" && (
              <ProgressTab
                lineItems={buildGcProgressDisplayItems(drawDetail)}
                loading={detailLoading}
              />
            )}
            {activeTab === "signatures" && (
              <SignaturesTab drawDetail={drawDetail} loading={detailLoading} />
            )}
            {activeTab === "inspection" && (
              <InspectionTab
                data={inspectionData}
                loading={inspectionLoading}
                onViewReport={handleViewReport}
                viewingReport={isViewingReport}
              />
            )}
            {activeTab === "timeline" && (
              <TimelineTab
                drawDetail={drawDetail}
                events={timelineEvents}
                loading={timelineLoading}
              />
            )}
            {activeTab === "approval" && (
              <ApprovalTab
                drawDetail={drawDetail}
                loading={detailLoading}
                onExportG702={handleExportG702}
                onExportExcel={handleExportExcel}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DrawHistorySection;
